/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  Competenza,
  GetCompetenzeParams,
  WsRispostaPaginataListCompetenza,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce una competenza
 * @summary Restituisce una competenza
 */
export const getCompetenzeId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Competenza>(
    { url: `/framework-cfg-utenti/competenze/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetCompetenzeIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/competenze/${id}`] as const;

export const getGetCompetenzeIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getCompetenzeId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenzeId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getCompetenzeId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCompetenzeIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCompetenzeId>>> = ({
    signal,
  }) => getCompetenzeId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetCompetenzeIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCompetenzeId>>
>;
export type GetCompetenzeIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce una competenza
 */
export const useGetCompetenzeId = <
  TData = Awaited<ReturnType<typeof getCompetenzeId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenzeId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetCompetenzeIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un competenza utente
 * @summary Aggiorna un competenza utente
 */
export const putCompetenzeId = (
  id: string,
  competenza: Competenza,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/competenze/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: competenza,
    },
    options,
  );
};

export const getPutCompetenzeIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putCompetenzeId>>,
    TError,
    { id: string; data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putCompetenzeId>>,
  TError,
  { id: string; data: Competenza },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putCompetenzeId>>,
    { id: string; data: Competenza }
  > = props => {
    const { id, data } = props ?? {};

    return putCompetenzeId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutCompetenzeIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putCompetenzeId>>
>;
export type PutCompetenzeIdMutationBody = Competenza;
export type PutCompetenzeIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un competenza utente
 */
export const usePutCompetenzeId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putCompetenzeId>>,
    TError,
    { id: string; data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutCompetenzeIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella un competenza utente
 * @summary Cancella un competenza utente
 */
export const deleteCompetenzeId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/competenze/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteCompetenzeIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCompetenzeId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteCompetenzeId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteCompetenzeId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteCompetenzeId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCompetenzeIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteCompetenzeId>>
>;

export type DeleteCompetenzeIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un competenza utente
 */
export const useDeleteCompetenzeId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteCompetenzeId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteCompetenzeIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cerca una serie di competenze. Ordinabile per idCompetenza, denominazione, codiceIstat.
 * @summary Cerca una serie di competenze
 */
export const getCompetenze = (
  params?: GetCompetenzeParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListCompetenza>(
    { url: `/framework-cfg-utenti/competenze`, method: 'get', params, signal },
    options,
  );
};

export const getGetCompetenzeQueryKey = (params?: GetCompetenzeParams) =>
  [`/framework-cfg-utenti/competenze`, ...(params ? [params] : [])] as const;

export const getGetCompetenzeQueryOptions = <
  TData = Awaited<ReturnType<typeof getCompetenze>>,
  TError = ErrorType<unknown>,
>(
  params?: GetCompetenzeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getCompetenze>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCompetenzeQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCompetenze>>> = ({
    signal,
  }) => getCompetenze(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetCompetenzeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCompetenze>>
>;
export type GetCompetenzeQueryError = ErrorType<unknown>;

/**
 * @summary Cerca una serie di competenze
 */
export const useGetCompetenze = <
  TData = Awaited<ReturnType<typeof getCompetenze>>,
  TError = ErrorType<unknown>,
>(
  params?: GetCompetenzeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetCompetenzeQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce una competenza ufficio
 * @summary Inserisce una competenza ufficio
 */
export const postCompetenze = (
  competenza: Competenza,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/competenze`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: competenza,
    },
    options,
  );
};

export const getPostCompetenzeMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCompetenze>>,
    TError,
    { data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postCompetenze>>,
  TError,
  { data: Competenza },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postCompetenze>>,
    { data: Competenza }
  > = props => {
    const { data } = props ?? {};

    return postCompetenze(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostCompetenzeMutationResult = NonNullable<
  Awaited<ReturnType<typeof postCompetenze>>
>;
export type PostCompetenzeMutationBody = Competenza;
export type PostCompetenzeMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce una competenza ufficio
 */
export const usePostCompetenze = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCompetenze>>,
    TError,
    { data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostCompetenzeMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Inserisce una competenza ufficio
 * @summary Inserisce una competenza ufficio
 */
export const postCompetenzeInsertForImport = (
  competenza: Competenza,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/competenze/insert-for-import`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: competenza,
    },
    options,
  );
};

export const getPostCompetenzeInsertForImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCompetenzeInsertForImport>>,
    TError,
    { data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postCompetenzeInsertForImport>>,
  TError,
  { data: Competenza },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postCompetenzeInsertForImport>>,
    { data: Competenza }
  > = props => {
    const { data } = props ?? {};

    return postCompetenzeInsertForImport(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostCompetenzeInsertForImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof postCompetenzeInsertForImport>>
>;
export type PostCompetenzeInsertForImportMutationBody = Competenza;
export type PostCompetenzeInsertForImportMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce una competenza ufficio
 */
export const usePostCompetenzeInsertForImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postCompetenzeInsertForImport>>,
    TError,
    { data: Competenza },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostCompetenzeInsertForImportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary chiamata btb. Cerca una serie di competenze collegate al codiceAoo
 */
export const getCompetenzeAooCodiceAoo = (
  codiceAoo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Competenza[]>(
    {
      url: `/framework-cfg-utenti/competenze/Aoo/${codiceAoo}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetCompetenzeAooCodiceAooQueryKey = (codiceAoo: string) =>
  [`/framework-cfg-utenti/competenze/Aoo/${codiceAoo}`] as const;

export const getGetCompetenzeAooCodiceAooQueryOptions = <
  TData = Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>,
  TError = ErrorType<unknown>,
>(
  codiceAoo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetCompetenzeAooCodiceAooQueryKey(codiceAoo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>
  > = ({ signal }) =>
    getCompetenzeAooCodiceAoo(codiceAoo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!codiceAoo, ...queryOptions };
};

export type GetCompetenzeAooCodiceAooQueryResult = NonNullable<
  Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>
>;
export type GetCompetenzeAooCodiceAooQueryError = ErrorType<unknown>;

/**
 * @summary chiamata btb. Cerca una serie di competenze collegate al codiceAoo
 */
export const useGetCompetenzeAooCodiceAoo = <
  TData = Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>,
  TError = ErrorType<unknown>,
>(
  codiceAoo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getCompetenzeAooCodiceAoo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetCompetenzeAooCodiceAooQueryOptions(
    codiceAoo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
